import { useEffect, useState, useCallback } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import {
  SOCKET_EVENTS,
  IntegrationSyncStartedEvent,
  IntegrationSyncProgressEvent,
  IntegrationSyncCompletedEvent,
  IntegrationSyncFailedEvent
} from '@troia-v3/types';
import { toast } from 'sonner';

interface CalendarSyncState {
  isSyncing: boolean;
  currentItem: number;
  totalItems: number;
  message: string;
}

interface UseCalendarSyncOptions {
  onSyncCompleted?: () => void;
  onSyncFailed?: (error: string) => void;
}

/**
 * Hook para gerenciar sincronização de calendário em tempo real via Socket.IO
 *
 * @param options - Callbacks opcionais para sync completed/failed
 * @returns Estado de sincronização e controles
 *
 * @example
 * const { isSyncing, currentItem, totalItems, message } = useCalendarSync({
 *   onSyncCompleted: () => {
 *     queryClient.invalidateQueries(['calendar-events']);
 *     toast.success('Calendário sincronizado!');
 *   }
 * });
 */
export const useCalendarSync = (options?: UseCalendarSyncOptions) => {
  const { socket, isConnected } = useSocket();
  const [syncState, setSyncState] = useState<CalendarSyncState>({
    isSyncing: false,
    currentItem: 0,
    totalItems: 0,
    message: ''
  });

  const handleSyncStarted = useCallback((data: IntegrationSyncStartedEvent) => {
    console.log('📅 [Calendar Sync] STARTED:', data);

    setSyncState({
      isSyncing: true,
      currentItem: 0,
      totalItems: data.totalItems || 0,
      message: 'Iniciando sincronização...'
    });

    toast.loading('Sincronizando calendário...', {
      id: 'calendar-sync',
      duration: Infinity
    });
  }, []);

  const handleSyncProgress = useCallback((data: IntegrationSyncProgressEvent) => {
    console.log('📅 [Calendar Sync] PROGRESS:', `${data.currentItem}/${data.totalItems}`);

    setSyncState(prev => ({
      ...prev,
      currentItem: data.currentItem,
      totalItems: data.totalItems,
      message: data.message || `Sincronizando ${data.currentItem}/${data.totalItems} eventos...`
    }));

    // Atualizar toast com progresso
    toast.loading(
      `Sincronizando ${data.currentItem}/${data.totalItems} eventos...`,
      {
        id: 'calendar-sync',
        duration: Infinity
      }
    );
  }, []);

  const handleSyncCompleted = useCallback((data: IntegrationSyncCompletedEvent) => {
    console.log('✅ [Calendar Sync] COMPLETED:', data);

    setSyncState({
      isSyncing: false,
      currentItem: data.itemsSynced,
      totalItems: data.itemsSynced,
      message: `${data.itemsSynced} eventos sincronizados`
    });

    // Remover loading toast e mostrar sucesso
    toast.dismiss('calendar-sync');
    toast.success(
      `${data.itemsSynced} ${data.itemsSynced === 1 ? 'evento sincronizado' : 'eventos sincronizados'}!`,
      { duration: 4000 }
    );

    // Callback de refresh
    if (options?.onSyncCompleted) {
      options.onSyncCompleted();
    }
  }, [options]);

  const handleSyncFailed = useCallback((data: IntegrationSyncFailedEvent) => {
    console.error('❌ [Calendar Sync] FAILED:', data);

    setSyncState({
      isSyncing: false,
      currentItem: data.itemsProcessed,
      totalItems: data.itemsProcessed,
      message: `Erro: ${data.error}`
    });

    // Remover loading toast e mostrar erro
    toast.dismiss('calendar-sync');
    toast.error(
      `Erro ao sincronizar: ${data.error}`,
      { duration: 6000 }
    );

    // Callback de erro
    if (options?.onSyncFailed) {
      options.onSyncFailed(data.error);
    }
  }, [options]);

  // ✅ Inscrever nos eventos Socket.IO
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    console.log('📅 [Calendar Sync] Subscribing to sync events...');

    // Adicionar listeners
    socket.on(SOCKET_EVENTS.INTEGRATION_SYNC_STARTED, handleSyncStarted);
    socket.on(SOCKET_EVENTS.INTEGRATION_SYNC_PROGRESS, handleSyncProgress);
    socket.on(SOCKET_EVENTS.INTEGRATION_SYNC_COMPLETED, handleSyncCompleted);
    socket.on(SOCKET_EVENTS.INTEGRATION_SYNC_FAILED, handleSyncFailed);

    // Cleanup: remover listeners
    return () => {
      console.log('📅 [Calendar Sync] Unsubscribing from sync events...');
      socket.off(SOCKET_EVENTS.INTEGRATION_SYNC_STARTED, handleSyncStarted);
      socket.off(SOCKET_EVENTS.INTEGRATION_SYNC_PROGRESS, handleSyncProgress);
      socket.off(SOCKET_EVENTS.INTEGRATION_SYNC_COMPLETED, handleSyncCompleted);
      socket.off(SOCKET_EVENTS.INTEGRATION_SYNC_FAILED, handleSyncFailed);
    };
  }, [socket, isConnected, handleSyncStarted, handleSyncProgress, handleSyncCompleted, handleSyncFailed]);

  return {
    ...syncState,
    isSocketConnected: isConnected
  };
};
